import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getLessonsMinimal } from '../../actions/lessonActions';
import { getCoursesMinimal } from '../../actions/courseActions';
import { getPathsMinimal } from '../../actions/pathActions';
import { X } from 'lucide-react';

const RARITY_OPTIONS = [
  { value: 'bronze', label: 'Bronze', color: '#CD7F32' },
  { value: 'silver', label: 'Silver', color: '#C0C0C0' },
  { value: 'gold', label: 'Gold', color: '#FFD700' },
  { value: 'platinum', label: 'Platinum', color: '#E5E4E2' }
];

const TRIGGER_OPTIONS = [
  { value: 'lesson_completion', label: 'Lesson Completion' },
  { value: 'course_completion', label: 'Course Completion' },
  { value: 'path_completion', label: 'Path Completion' },
  { value: 'manual', label: 'Manual Award' }
];

const BadgeForm = ({ badge, mode, onSubmit, onClose }) => {
  const dispatch = useDispatch();
  const { minimalLessons } = useSelector(state => state.lesson);
  const { coursesMinimal } = useSelector(state => state.course);
  const { minimalPaths } = useSelector(state => state.path);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [rarity, setRarity] = useState('bronze');
  const [triggerType, setTriggerType] = useState('course_completion');
  const [triggerTarget, setTriggerTarget] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [imageData, setImageData] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    dispatch(getLessonsMinimal());
    dispatch(getCoursesMinimal());
    dispatch(getPathsMinimal());
  }, [dispatch]);

  useEffect(() => {
    if (badge) {
      setTitle(badge.title || '');
      setDescription(badge.description || '');
      setRarity(badge.rarity || 'bronze');
      setTriggerType(badge.triggerType || 'course_completion');
      setTriggerTarget(badge.triggerTarget?._id || badge.triggerTarget || '');
      setImagePreview(badge.image || '');
      setIsActive(badge.isActive !== false);
    }
  }, [badge]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be under 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImageData(ev.target.result);
      setImagePreview(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      title,
      description,
      rarity,
      triggerType,
      isActive
    };
    if (triggerType !== 'manual') {
      data.triggerTarget = triggerTarget;
    }
    if (imageData) {
      data.image = imageData;
    }
    onSubmit(data);
  };

  const isViewMode = mode === 'view';

  const getTargetOptions = () => {
    switch (triggerType) {
      case 'lesson_completion':
        return (minimalLessons || []).map(l => ({ _id: l._id, title: l.title }));
      case 'course_completion':
        return (coursesMinimal || []).map(c => ({ _id: c._id, title: c.title }));
      case 'path_completion':
        return (minimalPaths || []).map(p => ({ _id: p._id, title: p.title }));
      default:
        return [];
    }
  };

  const selectedRarity = RARITY_OPTIONS.find(r => r.value === rarity);

  return (
    <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-[#054581]">
          {mode === 'new' ? 'Create Badge' : mode === 'edit' ? 'Edit Badge' : 'View Badge'}
        </h2>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-[#1086a4] transition-colors">
          <X className="h-6 w-6" />
        </button>
      </div>

      <div className="space-y-6">
        {/* Badge Preview */}
        <div className="flex items-center gap-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="relative">
            {imagePreview ? (
              <img
                src={imagePreview}
                alt="Badge"
                className="w-24 h-24 rounded-full object-cover border-4"
                style={{ borderColor: selectedRarity?.color || '#CD7F32' }}
              />
            ) : (
              <div
                className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center border-4"
                style={{ borderColor: selectedRarity?.color || '#CD7F32' }}
              >
                <span className="text-gray-400 text-3xl">?</span>
              </div>
            )}
          </div>
          <div>
            <h3 className="text-lg font-bold text-[#054581]">{title || 'Badge Name'}</h3>
            <span
              className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase mt-1"
              style={{ backgroundColor: selectedRarity?.color || '#CD7F32', color: '#333' }}
            >
              {rarity}
            </span>
            {description && <p className="text-gray-500 text-sm mt-1">{description}</p>}
          </div>
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-[#054581] mb-2">Title *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={isViewMode}
            required
            className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1086a4] disabled:opacity-60"
            placeholder="Enter badge title"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-[#054581] mb-2">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isViewMode}
            rows={3}
            className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1086a4] disabled:opacity-60"
            placeholder="Describe what this badge represents"
          />
        </div>

        {/* Image Upload */}
        <div>
          <label className="block text-sm font-medium text-[#054581] mb-2">Badge Image</label>
          {!isViewMode && (
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#1086a4] file:text-white hover:file:bg-[#054581]"
            />
          )}
        </div>

        {/* Rarity */}
        <div>
          <label className="block text-sm font-medium text-[#054581] mb-2">Rarity</label>
          <div className="flex gap-3">
            {RARITY_OPTIONS.map(option => (
              <button
                key={option.value}
                type="button"
                disabled={isViewMode}
                onClick={() => setRarity(option.value)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all border-2 ${
                  rarity === option.value
                    ? 'shadow-md scale-105'
                    : 'opacity-60 hover:opacity-80'
                }`}
                style={{
                  borderColor: option.color,
                  backgroundColor: rarity === option.value ? option.color : 'transparent',
                  color: rarity === option.value ? '#333' : '#666'
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Trigger Type */}
        <div>
          <label className="block text-sm font-medium text-[#054581] mb-2">Award When</label>
          <select
            value={triggerType}
            onChange={(e) => { setTriggerType(e.target.value); setTriggerTarget(''); }}
            disabled={isViewMode}
            className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1086a4] disabled:opacity-60"
          >
            {TRIGGER_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Trigger Target */}
        {triggerType !== 'manual' && (
          <div>
            <label className="block text-sm font-medium text-[#054581] mb-2">
              Select {triggerType === 'lesson_completion' ? 'Lesson' : triggerType === 'course_completion' ? 'Course' : 'Pathway'} *
            </label>
            <select
              value={triggerTarget}
              onChange={(e) => setTriggerTarget(e.target.value)}
              disabled={isViewMode}
              required
              className="w-full px-3 py-2 bg-[#f9fafb] border border-[#1086a4] rounded-md text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#1086a4] disabled:opacity-60"
            >
              <option value="">Select...</option>
              {getTargetOptions().map(opt => (
                <option key={opt._id} value={opt._id}>{opt.title}</option>
              ))}
            </select>
          </div>
        )}

        {/* Active Toggle */}
        {mode === 'edit' && (
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="w-4 h-4 text-[#1086a4] rounded"
            />
            <label className="text-sm font-medium text-[#054581]">Active</label>
          </div>
        )}

        {/* Submit */}
        {!isViewMode && (
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#1086a4] hover:bg-[#054581] text-white rounded-lg font-semibold transition-colors"
            >
              {mode === 'new' ? 'Create Badge' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>
    </form>
  );
};

export default BadgeForm;
